import React, { useEffect, useState } from 'react';
import useEditFormScroll, { editHighlightStyle } from '../hooks/useEditFormScroll';

function GstMaster({ token, selectedPropertyId }) {
  const emptyForm = { tax_code: '', tax_name: '', tax_regime: 'GST', tax_percent: '', cgst_percent: '', sgst_percent: '', igst_percent: '', is_active: true };
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState('');
  const { formRef, editHighlight, scrollToForm } = useEditFormScroll();
  const resolvePropertyId = () => String(selectedPropertyId || localStorage.getItem('selectedPropertyId') || '').trim();
  const authHeaders = (extra = {}) => ({ Authorization: `Bearer ${token}`, 'x-property-id': resolvePropertyId(), ...extra });

  useEffect(() => {
    if (!resolvePropertyId()) {
      setRows([]);
      setError('Select a property first from Hotel Config');
      return;
    }
    setError('');
    loadRows();
  }, [token, selectedPropertyId]);

  const loadRows = () => {
    if (!resolvePropertyId()) {
      setRows([]);
      setError('Select a property first from Hotel Config');
      return;
    }
    fetch('/api/tax-master', {
      headers: authHeaders()
    })
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) {
          setRows(data);
        } else if (data && Array.isArray(data.items)) {
          setRows(data.items);
        } else {
          setRows([]);
        }
      })
      .catch(() => {
        setError('Failed to load Tax master');
        setRows([]);
      });
  };

  const handleChange = e => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleCreate = async () => {
    if (!resolvePropertyId()) {
      setError('Select a property first from Hotel Config');
      return;
    }
    if (!form.tax_code || !form.tax_name) {
      setError('Tax code and tax name are required.');
      return;
    }
    if (form.tax_regime !== 'NONE' && !form.tax_percent && !form.cgst_percent && !form.sgst_percent && !form.igst_percent) {
      setError('Enter tax percent details.');
      return;
    }

    try {
      const res = await fetch('/api/tax-master', {
        method: 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(form)
      });
      if (res.ok) {
        const created = await res.json();
        setRows([...rows, created]);
        setForm(emptyForm);
        setError('');
      } else {
        const data = await res.json();
        setError(data.error || 'Create failed');
      }
    } catch {
      setError('Network error');
    }
  };

  const handleEdit = row => {
    setEditingId(row.id);
    setForm({
      tax_code: row.tax_code || '',
      tax_name: row.tax_name || '',
      tax_regime: row.tax_regime || 'GST',
      tax_percent: row.tax_percent ?? '',
      cgst_percent: row.cgst_percent ?? '',
      sgst_percent: row.sgst_percent ?? '',
      igst_percent: row.igst_percent ?? '',
      is_active: row.is_active !== false
    });
    scrollToForm();
  };

  const handleUpdate = async () => {
    if (!resolvePropertyId()) {
      setError('Select a property first from Hotel Config');
      return;
    }
    if (!form.tax_code || !form.tax_name) {
      setError('Tax code and tax name are required.');
      return;
    }
    if (form.tax_regime !== 'NONE' && !form.tax_percent && !form.cgst_percent && !form.sgst_percent && !form.igst_percent) {
      setError('Enter tax percent details.');
      return;
    }

    try {
      const res = await fetch(`/api/tax-master/${editingId}`, {
        method: 'PUT',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(form)
      });
      if (res.ok) {
        const updated = await res.json();
        setRows(rows.map(r => r.id === editingId ? updated : r));
        setForm(emptyForm);
        setEditingId(null);
        setError('');
      } else {
        const data = await res.json();
        setError(data.error || 'Update failed');
      }
    } catch {
      setError('Network error');
    }
  };

  const handleDelete = async id => {
    if (!resolvePropertyId()) {
      setError('Select a property first from Hotel Config');
      return;
    }
    try {
      const res = await fetch(`/api/tax-master/${id}`, {
        method: 'DELETE',
        headers: authHeaders()
      });
      if (res.ok) {
        setRows(rows.filter(r => r.id !== id));
        setError('');
      } else {
        const data = await res.json();
        setError(data.error || 'Delete failed');
      }
    } catch {
      setError('Network error');
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setForm(emptyForm);
    setError('');
  };

  const totalPercent = (f = form) => {
    if (String(f.tax_regime || '').toUpperCase() !== 'GST') return Number(f.tax_percent || 0).toFixed(2);
    const cg = Number(f.cgst_percent || 0);
    const sg = Number(f.sgst_percent || 0);
    const ig = Number(f.igst_percent || 0);
    return (cg + sg + ig).toFixed(2);
  };

  return (
    <div className="page-shell">
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.78rem', marginBottom: '0.6rem' }}>
        <span style={{ color: '#6b7280', fontWeight: 500 }}>Hotel Config</span>
        <span style={{ color: '#cbd5e0' }}>›</span>
        <span style={{ color: 'var(--theme-primary-dark)', fontWeight: 600 }}>Tax Master</span>
      </div>
      <h2 style={{ color: 'var(--theme-primary-dark)' }}>Tax Master</h2>
      <p style={{ color: '#6b7280', marginBottom: '1.5rem', fontSize: '0.9rem' }}>Manage tax codes, rates and GST components used for billing</p>
      {error && <div className="error-banner">{error}</div>}
      <div className="filter-bar" ref={formRef} style={{ marginBottom: '2rem', ...editHighlightStyle(editHighlight) }}>
        <h3 style={{ marginTop: 0, marginBottom: '1rem', color: 'var(--theme-text)' }}>{editingId ? 'Edit Tax Profile' : 'New Tax Profile'}</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem' }}>
          <div>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: 4, color: 'var(--theme-text)' }}>Tax Code</label>
            <input name="tax_code" placeholder="e.g. LIQUOR_VAT" value={form.tax_code} onChange={handleChange} style={{ width: '100%', padding: '8px' }} />
          </div>
          <div>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: 4, color: 'var(--theme-text)' }}>Tax Name</label>
            <input name="tax_name" placeholder="Tax Name" value={form.tax_name} onChange={handleChange} style={{ width: '100%', padding: '8px' }} />
          </div>
          <div>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: 4, color: 'var(--theme-text)' }}>Regime</label>
            <select name="tax_regime" value={form.tax_regime} onChange={handleChange} style={{ width: '100%', padding: '8px' }}>
              <option value="GST">GST</option>
              <option value="VAT">VAT</option>
              <option value="SALES_TAX">Sales Tax</option>
              <option value="NONE">No Tax</option>
            </select>
          </div>
          <div>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: 4, color: 'var(--theme-text)' }}>Tax %</label>
            <input name="tax_percent" placeholder="For VAT / Sales Tax" value={form.tax_percent} onChange={handleChange} disabled={String(form.tax_regime).toUpperCase() === 'GST'} style={{ width: '100%', padding: '8px', opacity: String(form.tax_regime).toUpperCase() === 'GST' ? 0.6 : 1 }} />
          </div>
          <div>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: 4, color: 'var(--theme-text)' }}>CGST %</label>
            <input name="cgst_percent" placeholder="CGST %" value={form.cgst_percent} onChange={handleChange} disabled={String(form.tax_regime).toUpperCase() !== 'GST'} style={{ width: '100%', padding: '8px', opacity: String(form.tax_regime).toUpperCase() !== 'GST' ? 0.6 : 1 }} />
          </div>
          <div>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: 4, color: 'var(--theme-text)' }}>SGST %</label>
            <input name="sgst_percent" placeholder="SGST %" value={form.sgst_percent} onChange={handleChange} disabled={String(form.tax_regime).toUpperCase() !== 'GST'} style={{ width: '100%', padding: '8px', opacity: String(form.tax_regime).toUpperCase() !== 'GST' ? 0.6 : 1 }} />
          </div>
          <div>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: 4, color: 'var(--theme-text)' }}>IGST %</label>
            <input name="igst_percent" placeholder="IGST %" value={form.igst_percent} onChange={handleChange} disabled={String(form.tax_regime).toUpperCase() !== 'GST'} style={{ width: '100%', padding: '8px', opacity: String(form.tax_regime).toUpperCase() !== 'GST' ? 0.6 : 1 }} />
          </div>
          <div>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: 4, color: 'var(--theme-text)' }}>Total Tax %</label>
            <input value={totalPercent()} readOnly style={{ width: '100%', padding: '8px', opacity: 0.6 }} />
          </div>
        </div>
        <div style={{ marginTop: '1rem', display: 'flex', gap: '1rem' }}>
          {editingId ? (
            <>
              <button type="button" onClick={handleUpdate} className="btn-success">Update Tax</button>
              <button type="button" onClick={handleCancel} className="btn-muted">Cancel</button>
            </>
          ) : (
            <button type="button" onClick={handleCreate} className="btn-primary">Add Tax</button>
          )}
        </div>
      </div>
      <h3 style={{ color: 'var(--theme-primary-dark)', marginBottom: '0.75rem' }}>Tax Profiles</h3>
      {!rows || rows.length === 0 ? (
        <div style={{ color: '#9ca3af', textAlign: 'center', padding: '2rem', background: '#fafafa', borderRadius: '8px', border: '1px dashed var(--theme-border)' }}>
          No tax profiles found. Add your first tax profile above.
        </div>
      ) : (
        <div className="card" style={{ overflowX: 'auto', marginTop: '1.5rem' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
            <thead>
              <tr style={{ background: 'var(--theme-primary-dark)', color: '#fff' }}>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left' }}>Code</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left' }}>Name</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left' }}>Regime</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left' }}>CGST %</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left' }}>SGST %</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left' }}>IGST %</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left' }}>Total Tax %</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(row => (
                <tr key={row.id} style={{ borderBottom: '1px solid #dcdde1' }}>
                  <td style={{ padding: '0.75rem 1rem' }}>{row.tax_code || '-'}</td>
                  <td style={{ padding: '0.75rem 1rem' }}>{row.tax_name || '-'}</td>
                  <td style={{ padding: '0.75rem 1rem' }}>{row.tax_regime || '-'}</td>
                  <td style={{ padding: '0.75rem 1rem' }}>{Number(row.cgst_percent || 0).toFixed(2)}%</td>
                  <td style={{ padding: '0.75rem 1rem' }}>{Number(row.sgst_percent || 0).toFixed(2)}%</td>
                  <td style={{ padding: '0.75rem 1rem' }}>{Number(row.igst_percent || 0).toFixed(2)}%</td>
                  <td style={{ padding: '0.75rem 1rem' }}>{(String(row.tax_regime||'').toUpperCase() === 'GST' ? (Number(row.cgst_percent||0)+Number(row.sgst_percent||0)+Number(row.igst_percent||0)).toFixed(2) : Number(row.tax_percent||0).toFixed(2))}%</td>
                  <td style={{ padding: '0.75rem 1rem', display: 'flex', gap: '0.5rem' }}>
                    <button onClick={() => handleEdit(row)} className="btn-primary btn-sm">Edit</button>
                    <button onClick={() => handleDelete(row.id)} className="btn-danger btn-sm">Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default GstMaster;
